// The console: sign-in gate, then the shell and the tab in view. Data comes
// from mygaz-dev (ops_* reads + Realtime) or, behind the "Démo" pill, the
// design's example set.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import { demoSource, fetchMe, liveSource, type Source } from './lib/api';
import { useData } from './lib/useData';
import { S } from './lib/strings';
import type { AgencyMe, Me } from './lib/types';
import { demoAgency, liveAgency } from './lib/agency-api';
import { AgencyApp } from './agency/AgencyApp';
import { Shell, TABS, type Tab } from './components/Shell';
import { applyTheme, readTheme, saveTheme, THEMES, type Theme } from './lib/theme';
import { forgetSignedIn, isStale, markSignedIn, signedInAt } from './lib/session';
import { Palette } from './components/Palette';
import { Gazelle } from './components/Gazelle';
import { OrderDrawer } from './components/OrderDrawer';
import { useRemind } from './components/OrderActions';
import { Ic } from './components/Icon';
import { Overview } from './views/Overview';
import { Orders } from './views/Orders';
import { Live } from './views/Live';
import { Soon } from './views/Soon';
import { Homes } from './views/Homes';
import { Devices } from './views/Devices';
import { Finances } from './views/Finances';
import { Agences } from './views/Agences';
import { Marketers } from './views/Marketers';
import { Login } from './views/Login';
import { LangSwitch } from './components/Lang';
import { Loading } from './components/Loading';
import { Bell } from './components/Bell';
import { SplashGate } from './components/Splash';

const tabFromHash = (): Tab => { const h = location.hash.slice(1); return (TABS.some(([k]) => k === h) ? h : 'ov') as Tab; };
// No demo on the live console (Ruben, 2 Nov 2026: nobody sees inside without
// signing in). The example data opens only on a developer's machine (?demo)
// or in a design-review build made with VITE_DEMO_ONLY=1.
const DEMO_ONLY = import.meta.env.VITE_DEMO_ONLY === '1';
const DEMO_OK = DEMO_ONLY || import.meta.env.DEV;
const wantsDemo = () => DEMO_ONLY || (DEMO_OK && new URLSearchParams(location.search).has('demo'));
// ?demo=agence opens the agency's own space on the example data (developer machine only).
const wantsAgencyDemo = () => DEMO_OK && new URLSearchParams(location.search).get('demo') === 'agence';

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  // A dispatcher signs in with the phone they use in MyGaz Pro (D-56) and gets their agency's space.
  const [agency, setAgency] = useState<AgencyMe | null | undefined>(undefined);
  const [demo, setDemo] = useState(wantsDemo);
  const [expired, setExpired] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!supabase) { setMe(null); setChecked(true); return; }
    const sb = supabase;
    // One WhatsApp code keeps this computer signed in for 30 days, then asks
    // for a new one. A session from before the rule gets its 30 days from now.
    const keep = (s: Session | null) => {
      if (!s) return null;
      const at = signedInAt();
      if (at === null) { markSignedIn(); return s; }
      if (!isStale(at, Date.now())) return s;
      forgetSignedIn();
      setExpired(true);
      setTimeout(() => void sb.auth.signOut(), 0); // never inside the auth callback itself
      return null;
    };
    sb.auth.getSession().then(({ data }) => { setSession(keep(data.session)); setChecked(true); });
    const { data } = sb.auth.onAuthStateChange((e, s) => {
      if (e === 'SIGNED_OUT') forgetSignedIn();
      setSession(keep(s));
    });
    // A tab left open for days is checked again when it comes back to the front.
    const onBack = () => {
      if (document.visibilityState === 'visible') sb.auth.getSession().then(({ data: d }) => { if (d.session && !keep(d.session)) setSession(null); });
    };
    document.addEventListener('visibilitychange', onBack);
    return () => { data.subscription.unsubscribe(); document.removeEventListener('visibilitychange', onBack); };
  }, []);
  useEffect(() => {
    if (!session) { setMe(null); setAgency(null); return; }
    setMe(undefined); setAgency(undefined);
    fetchMe()
      .then(async (m) => { setMe(m); setAgency(m ? null : await liveAgency.me().catch(() => null)); })
      .catch(() => { setMe(null); setAgency(null); });
  }, [session]);

  const enterDemo = () => { setDemo(true); history.replaceState(null, '', '?demo' + location.hash); };
  const leaveDemo = () => { setDemo(false); history.replaceState(null, '', location.pathname + location.hash); };
  const signOut = () => { supabase?.auth.signOut(); };

  // The splash covers the session check, the admin check and the console's first load.
  const [loaded, setLoaded] = useState(false);
  const onLoaded = useCallback(() => setLoaded(true), []);
  // The bar's real steps: the session checked, the admin found, then the console's three first reads.
  const [parts, setParts] = useState(0);
  const progress = demo ? parts / 3 : !checked ? 0 : !session ? 1 : (1 + (me !== undefined ? 1 : 0) + parts) / 5;
  let body: React.ReactNode;
  let ready = true;
  if (demo && wantsAgencyDemo()) { body = <AgencyApp src={demoAgency} me={{ profile_id: 'demo', name: 'Express Delivery', phone: '237670000010', agency: { id: 'demo-ag-ed', name: 'Express Delivery' } }} onLeaveDemo={DEMO_ONLY ? undefined : leaveDemo} />; }
  else if (demo) { body = <Console src={demoSource} who="Ruben Bisso" demo onLeaveDemo={DEMO_ONLY ? undefined : leaveDemo} onLoaded={onLoaded} onProgress={setParts} />; ready = loaded; }
  else if (session && (me === undefined || (me === null && agency === undefined))) { body = null; ready = false; }
  else if (session && !me && agency) { body = <AgencyApp src={liveAgency} me={agency} onSignOut={signOut} />; }
  else if (session && me) { body = <Console src={liveSource} who={me.name} onSignOut={signOut} onLoaded={onLoaded} onProgress={setParts} />; ready = loaded; }
  else body = <Login onDemo={DEMO_OK ? enterDemo : undefined} notAdmin={!!session && me === null} expired={expired} onSignOut={signOut} />;
  return <>{body}<SplashGate key={demo ? 'd' : session ? 's' : 'l'} ready={ready} progress={progress} /></>;
}

function Console({ src, who, demo, onLeaveDemo, onSignOut, onLoaded, onProgress }: { src: Source; who: string; demo?: boolean; onLeaveDemo?(): void; onSignOut?(): void; onLoaded(): void; onProgress(n: number): void }) {
  const data = useData(src);
  useEffect(() => { onProgress(data.parts); }, [data.parts, onProgress]);
  const remind = useRemind(src, data.reload);
  useEffect(() => { if (data.overview || data.error) onLoaded(); }, [data.overview, data.error, onLoaded]);
  const [tab, setTab] = useState<Tab>(tabFromHash);
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [kOpen, setKOpen] = useState(false);
  const [gzOpen, setGzOpen] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [homeId, setHomeId] = useState<string | null>(null);

  useEffect(() => {
    const onHash = () => { setTab(tabFromHash()); setOrderId(null); };
    addEventListener('hashchange', onHash);
    return () => removeEventListener('hashchange', onHash);
  }, []);
  useEffect(() => { applyTheme(theme); }, [theme]);

  // Each tab keeps its scroll: leaving one remembers where it was, coming back restores it.
  const scrolls = useRef<Partial<Record<Tab, number>>>({});
  const shownTab = useRef<Tab>(tab);
  const go = useCallback((t: Tab) => {
    scrolls.current[shownTab.current] = window.scrollY;
    setTab(t);
    setOrderId(null);
    try { history.replaceState(null, '', location.search + '#' + t); } catch { /* sandboxed preview */ }
  }, []);
  useLayoutEffect(() => {
    if (shownTab.current === tab) return;
    shownTab.current = tab;
    window.scrollTo({ top: scrolls.current[tab] ?? 0 });
  }, [tab]);

  // Tabs stay mounted once opened, so coming back is instant: a hidden tab keeps
  // the data stamp it last saw, and refreshes in the background when shown again.
  const [opened, setOpened] = useState<Tab[]>(() => [tab]);
  const [stamps, setStamps] = useState<Partial<Record<Tab, Date | null>>>({});
  useEffect(() => {
    setOpened((o) => (o.includes(tab) ? o : [...o, tab]));
    setStamps((s) => (s[tab] === data.updatedAt ? s : { ...s, [tab]: data.updatedAt }));
  }, [tab, data.updatedAt]);
  const rf = (t: Tab) => (t === tab ? data.updatedAt : stamps[t] ?? data.updatedAt);
  const openOrder = useCallback((id: string) => { setOrderId(id); setGzOpen(false); }, []);
  const [gzAsk, setGzAsk] = useState<{ q: string; n: number } | null>(null);
  const openGz = useCallback((q?: string) => { setGzOpen(true); setOrderId(null); if (q) setGzAsk((a) => ({ q, n: (a?.n ?? 0) + 1 })); }, []);
  const openHome = useCallback((id: string) => { setHomeId(id); go('hh'); }, [go]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === 'k') { e.preventDefault(); setKOpen((v) => !v); return; }
      if ((e.metaKey || e.ctrlKey) && k === 'j') { e.preventDefault(); setGzOpen((v) => !v); return; }
      if (e.key === 'Escape') { if (orderId) setOrderId(null); else if (gzOpen) setGzOpen(false); return; }
      const tag = ((e.target as HTMLElement)?.tagName || '').toLowerCase();
      if (tag === 'input' || e.metaKey || e.ctrlKey || e.altKey || kOpen) return;
      const n = +e.key;
      if (n >= 1 && n <= 8) go(TABS[n - 1][0]);
      if (e.key === '/') { e.preventDefault(); setKOpen(true); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [orderId, gzOpen, kOpen, go]);

  const order = useMemo(() => {
    if (!orderId) return null;
    const all = [...(data.orders ?? []), ...(data.overview?.soon_empty.flatMap((h) => (h.open_order ? [h.open_order] : [])) ?? [])];
    return all.find((o) => o.id === orderId) ?? null;
  }, [orderId, data.orders, data.overview]);

  const alerts = data.overview?.alerts.length ?? 0;
  const pane = (t: Tab): React.ReactNode => {
    switch (t) {
      case 'ov': return <Overview ov={data.overview!} live={data.live} who={who} updatedAt={data.updatedAt} go={go} openOrder={openOrder} openGz={openGz} openHome={openHome} remind={remind} />;
      case 'ord': return <Orders orders={data.orders!} openOrder={openOrder} />;
      case 'live': return <Live live={data.live} updatedAt={data.updatedAt} openOrder={openOrder} openHome={openHome} orders={data.orders} />;
      case 'hh': return <Homes src={src} refresh={rf('hh')} selected={homeId} onSelect={setHomeId} openOrder={openOrder} go={go} openGz={openGz} />;
      case 'dev': return <Devices src={src} refresh={rf('dev')} openHome={openHome} openOrder={openOrder} />;
      case 'fin': return <Finances src={src} refresh={rf('fin')} go={go} openOrder={openOrder} />;
      case 'mk': return <Marketers src={src} refresh={rf('mk')} />;
      case 'team': return <Agences src={src} refresh={rf('team')} openOrder={openOrder} />;
      default: return <Soon tab={t} />;
    }
  };
  let view: React.ReactNode;
  if (data.error && !data.overview) {
    view = <div className="card empty" style={{ marginTop: 24 }}><b>{S.errors.load}</b><span className="mono">{data.error}</span><div style={{ marginTop: 14 }}><button className="btn" onClick={data.reload}>{S.errors.retry}</button></div></div>;
  } else if (!data.overview || !data.orders) {
    view = <Loading />;
  } else {
    view = (opened.includes(tab) ? opened : [...opened, tab]).map((t) => <div key={t} className="pane" hidden={t !== tab}>{pane(t)}</div>);
  }

  return (
    <>
      <Shell tab={tab} onTab={go} alerts={alerts} theme={theme} onTheme={() => { const t = THEMES[(THEMES.indexOf(theme) + 1) % 3]; setTheme(t); saveTheme(t); }}
        onSearch={() => setKOpen(true)} onGazelle={() => setGzOpen((v) => !v)} who={who}
        bell={<Bell alerts={data.overview?.alerts ?? null} onOrder={openOrder} onHome={openHome} onRemind={remind}
          onTodo={() => { go('ov'); setTimeout(() => document.getElementById('ov-todo')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 80); }} />} />
      <main>
        {demo && <div className="demo-bar"><span className="st violet">{S.demoPill}</span>{onLeaveDemo && <button className="link" onClick={onLeaveDemo}>{S.demoLeave}</button>}</div>}
        <div className="panes">{view}</div>
        <div className="ops-foot">{onSignOut && <button className="link" onClick={onSignOut}><Ic n="undo" s="xs" />{S.login.signOut}</button>}<LangSwitch /></div>
      </main>
      <OrderDrawer order={order} onClose={() => setOrderId(null)} onHome={openHome} src={src} onChanged={data.reload} />
      <Gazelle open={gzOpen} onClose={() => setGzOpen(false)} src={src} refresh={data.updatedAt} tab={tab} ask={gzAsk} onChanged={data.reload} openOrder={openOrder} who={who} />
      <Palette open={kOpen} onClose={() => setKOpen(false)} orders={data.orders ?? []} src={src}
        onTab={go} onOrder={openOrder} onHome={openHome} onAsk={(q) => openGz(q)} />
    </>
  );
}
